import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { authForgot, authReset, authInviteInfo, formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ShieldCheck, Sparkles, Mail, Lock, User, Building2, ArrowRight, CheckCircle2 } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { LegalFooter } from "@/components/LegalFooter";

const Shell = ({ title, subtitle, children, footer }) => (
  <div className="min-h-screen bg-background text-foreground grid lg:grid-cols-[1.1fr_1fr]">
    <aside className="hidden lg:flex flex-col justify-between p-14 bg-[#0b0d14] text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.15] bg-[radial-gradient(circle_at_20%_20%,#6366f1,transparent_40%),radial-gradient(circle_at_80%_70%,#10b981,transparent_35%)]" />
      <div className="relative"><BrandLogo size="lg" light /></div>
      <div className="relative max-w-md space-y-6">
        <h1 className="text-4xl font-bold leading-tight">AI-coded supplier invoices, <span className="text-primary">reviewed by your team</span> — not typed by it.</h1>
        <ul className="space-y-3 text-sm text-white/70">
          {["Extracts every field from PDFs, images and forwarded emails", "Suggests GL codes and flags anything under 80% confidence", "Full audit trail of who approved what, month-end pack included"].map((t) => (
            <li key={t} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />{t}</li>
          ))}
        </ul>
      </div>
      <div className="relative text-xs text-white/40 flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Each company gets an isolated workspace. 14-day free trial on the Business tier. <Link to="/pricing" data-testid="link-pricing" className="text-primary hover:underline ml-1">See pricing</Link></div>
    </aside>
    <main className="flex flex-col min-h-screen">
      <div className="flex flex-1 items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><BrandLogo /></div>
          <h2 className="text-2xl font-bold tracking-tight" data-testid="auth-title">{title}</h2>
          {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
          <div className="mt-8 space-y-4">{children}</div>
          {footer && <div className="mt-8 text-sm text-muted-foreground">{footer}</div>}
        </div>
      </div>
      <LegalFooter compact className="shrink-0" />
    </main>
  </div>
);

const Field = ({ icon: Icon, ...props }) => (
  <div className="relative">
    <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
    <Input {...props} className="pl-9 h-11" />
  </div>
);

const ErrorBox = ({ error }) => error ? <div data-testid="auth-error" className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-500">{error}</div> : null;

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await login(email, password);
      const next = params.get("next");
      if (next && next.startsWith("/api/")) window.location.href = `${window.location.origin}${next}`;
      else navigate(next && next.startsWith("/") ? next : "/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
    setBusy(false);
  };

  return (
    <Shell title="Sign in to D20" subtitle="Welcome back — your review queue is waiting."
           footer={<>New company? <Link data-testid="link-register" to="/register" className="text-primary font-medium hover:underline">Start a free trial</Link></>}>
      <form onSubmit={submit} className="space-y-3" data-testid="login-form">
        <Field icon={Mail} data-testid="login-email" type="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
        <Field icon={Lock} data-testid="login-password" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        <ErrorBox error={error} />
        <Button data-testid="login-submit" type="submit" className="w-full h-11" disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4 ml-2" /></>}</Button>
        <div className="text-right"><Link data-testid="link-forgot" to="/forgot-password" className="text-xs text-muted-foreground hover:text-foreground">Forgot password?</Link></div>
      </form>
    </Shell>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const inviteToken = params.get("invite");
  const [invite, setInvite] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", company: "", password: "" });
  const [accept, setAccept] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!inviteToken) return;
    authInviteInfo(inviteToken).then((i) => { setInvite(i); setForm((f) => ({ ...f, email: i.email })); })
      .catch((e) => setError(formatApiError(e.response?.data?.detail)));
  }, [inviteToken]);

  const submit = async (e) => {
    e.preventDefault();
    if (!accept) { setError("Please accept the Terms of Service and Privacy Policy to continue."); return; }
    setBusy(true); setError("");
    try {
      await register({ ...form, invite_token: inviteToken || undefined, accept_terms: true });
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
    setBusy(false);
  };

  return (
    <Shell title={invite ? `Join ${invite.org_name}` : "Create your workspace"}
           subtitle={invite ? `${invite.invited_by} invited you as a ${invite.role}.` : "Free 14-day trial on the Business tier. No card needed."}
           footer={<>Already have an account? <Link data-testid="link-login" to="/login" className="text-primary font-medium hover:underline">Sign in</Link></>}>
      <form onSubmit={submit} className="space-y-3" data-testid="register-form">
        <Field icon={User} data-testid="register-name" placeholder="Your full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus />
        <Field icon={Mail} data-testid="register-email" type="email" placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required disabled={!!invite} />
        {!invite && <Field icon={Building2} data-testid="register-company" placeholder="Company name" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />}
        <Field icon={Lock} data-testid="register-password" type="password" placeholder="Password (min 8 characters)" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
        <div className="pt-1 space-y-1.5" data-testid="register-legal">
          <label htmlFor="register-accept" className="flex items-start gap-2.5 text-sm cursor-pointer">
            <Checkbox id="register-accept" checked={accept} onCheckedChange={(v) => { setAccept(v === true); if (v === true) setError(""); }} aria-required="true" data-testid="register-accept-terms" className="mt-0.5" />
            <span>
              I agree to the{" "}
              <a href="/legal/terms" target="_blank" rel="noreferrer" className="text-primary underline underline-offset-2 hover:opacity-80" data-testid="register-terms-link">Terms of Service</a>
              {" "}and acknowledge the{" "}
              <a href="/legal/privacy" target="_blank" rel="noreferrer" className="text-primary underline underline-offset-2 hover:opacity-80" data-testid="register-privacy-link">Privacy Policy</a>.
            </span>
          </label>
          <p className="pl-[26px] text-xs text-muted-foreground" data-testid="register-authority-note">By creating an account, you confirm that you are authorised to act for your organisation.</p>
        </div>
        <ErrorBox error={error} />
        <Button data-testid="register-submit" type="submit" className="w-full h-11" disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-2" /> {invite ? "Accept & join" : "Create workspace"}</>}</Button>
      </form>
    </Shell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState(() => new URLSearchParams(window.location.search).get("email") || "");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => { e.preventDefault(); setBusy(true); try { await authForgot(email); } finally { setDone(true); setBusy(false); } };
  return (
    <Shell title="Reset your password" subtitle="We'll email you a one-time link valid for 1 hour."
           footer={<Link data-testid="link-login" to="/login" className="text-primary font-medium hover:underline">Back to sign in</Link>}>
      {done ? <div data-testid="forgot-done" className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-3 text-sm">If that email has an account, a reset link is on its way.</div> : (
        <form onSubmit={submit} className="space-y-3" data-testid="forgot-form">
          <Field icon={Mail} data-testid="forgot-email" type="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
          <Button data-testid="forgot-submit" type="submit" className="w-full h-11" disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}</Button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try { setUser(await authReset(params.get("token") || "", password)); navigate("/"); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <Shell title="Choose a new password" footer={<Link to="/login" className="text-primary font-medium hover:underline">Back to sign in</Link>}>
      <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
        <Field icon={Lock} data-testid="reset-password" type="password" placeholder="New password (min 8 characters)" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus />
        <ErrorBox error={error} />
        <Button data-testid="reset-submit" type="submit" className="w-full h-11" disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save new password"}</Button>
      </form>
    </Shell>
  );
}
